import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { truckService } from '../api/api';

const MAX_IMAGES = 12;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
const CATEGORY_OPTIONS = [
  { value: 'before_repair', label: 'Before repair' },
  { value: 'after_repair', label: 'After repair' },
  { value: 'damage_issue', label: 'Damage or issue' },
  { value: 'follow_up_required', label: 'Follow-up required' },
  { value: 'other', label: 'Other' },
];

function categoryLabel(value) {
  return CATEGORY_OPTIONS.find((option) => option.value === value)?.label || 'Other';
}

function dateTimeLabel(value) {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown time' : date.toLocaleString();
}

function defaultMetadata() {
  return {
    description: '',
    category: 'after_repair',
    service_item: '',
    requires_attention: false,
    follow_up_instructions: '',
  };
}

function imagePayload(image, overrides = {}) {
  return {
    description: image.description || '',
    category: image.category || 'other',
    service_item: image.service_item || '',
    requires_attention: image.requires_attention === true,
    follow_up_instructions: image.follow_up_instructions || '',
    resolved: image.resolved === true,
    ...overrides,
  };
}

function SecureThumbnail({ serviceLogId, image, onOpen }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setSrc('');
    setFailed(false);
    truckService.getServiceLogPhotoContent(serviceLogId, image.id, { thumbnail: true })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setSrc(objectUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [image.id, serviceLogId]);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') onOpen(); }}
      sx={{
        position: 'relative',
        aspectRatio: '4 / 3',
        bgcolor: '#e8eef6',
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {src ? (
        <Box component="img" src={src} alt={image.description || image.original_filename || 'Repair attachment'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        failed ? <ImageIcon sx={{ fontSize: 44, color: '#94a3b8' }} /> : <CircularProgress size={28} />
      )}
      <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,31,63,0)', transition: 'background-color .2s', '&:hover': { bgcolor: 'rgba(0,31,63,.3)' }, '&:hover .previewIcon': { opacity: 1 } }}>
        <VisibilityIcon className="previewIcon" sx={{ color: 'white', opacity: 0, transition: 'opacity .2s' }} />
      </Box>
    </Box>
  );
}

export default function ServiceLogAttachments({ serviceLogId, initialImages = null, onChange }) {
  const [images, setImages] = useState(Array.isArray(initialImages) ? initialImages : []);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(!Array.isArray(initialImages));
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editImage, setEditImage] = useState(null);
  const [editForm, setEditForm] = useState(defaultMetadata());
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteImage, setDeleteImage] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const pendingRef = useRef([]);

  const replaceImages = (next) => {
    setImages(next);
    onChange?.(next);
  };

  useEffect(() => {
    if (Array.isArray(initialImages)) {
      setImages(initialImages);
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    truckService.getServiceLogPhotos(serviceLogId)
      .then((response) => active && setImages(Array.isArray(response.data) ? response.data : []))
      .catch((uploadError) => active && setError(uploadError?.response?.data?.error || 'Failed to load repair images.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [initialImages, serviceLogId]);

  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  useEffect(() => () => {
    pendingRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const addFiles = (fileList) => {
    setError('');
    setSuccess('');
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - images.length - pending.length;
    if (remaining <= 0) {
      setError(`A service log can contain up to ${MAX_IMAGES} images.`);
      return;
    }
    const accepted = [];
    for (const file of files.slice(0, remaining)) {
      const extension = `.${String(file.name || '').split('.').pop()?.toLowerCase()}`;
      if (!ACCEPTED_TYPES.includes(file.type) || !ACCEPTED_EXTENSIONS.includes(extension)) {
        setError('Only JPEG, PNG, WebP, HEIC, and HEIF repair images are supported.');
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        setError(`${file.name} is larger than 10MB.`);
        continue;
      }
      accepted.push({
        key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        previewUrl: URL.createObjectURL(file),
        ...defaultMetadata(),
      });
    }
    if (files.length > remaining) setError(`Only ${remaining} more image${remaining === 1 ? '' : 's'} can be added.`);
    if (accepted.length) setPending((current) => [...current, ...accepted]);
  };

  const updatePending = (key, field, value) => {
    setPending((current) => current.map((item) => item.key === key ? { ...item, [field]: value } : item));
  };

  const removePending = (key) => {
    setPending((current) => {
      const item = current.find((entry) => entry.key === key);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return current.filter((entry) => entry.key !== key);
    });
  };

  const uploadPending = async () => {
    if (!pending.length || uploading) return;
    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      pending.forEach((item) => formData.append('images', item.file));
      formData.append('metadata', JSON.stringify(pending.map((item) => imagePayload(item))));
      const response = await truckService.uploadServiceLogPhotos(serviceLogId, formData, (event) => {
        if (event.total) setUploadProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
      });
      const uploaded = Array.isArray(response.data?.images) ? response.data.images : [];
      replaceImages([...uploaded, ...images]);
      pending.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
      setPending([]);
      setSuccess(`${uploaded.length} repair image${uploaded.length === 1 ? '' : 's'} uploaded successfully.`);
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (uploadError) {
      setError(uploadError?.response?.data?.error || uploadError?.message || 'Failed to upload repair images.');
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (image) => {
    setEditImage(image);
    setEditForm(imagePayload(image));
    setError('');
  };

  const saveEdit = async () => {
    if (!editImage || savingEdit) return;
    setSavingEdit(true);
    setError('');
    try {
      const response = await truckService.updateServiceLogPhoto(serviceLogId, editImage.id, editForm);
      replaceImages(images.map((image) => image.id === editImage.id ? response.data : image));
      setEditImage(null);
      setSuccess('Image information updated successfully.');
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (editError) {
      setError(editError?.response?.data?.error || 'Failed to update image information.');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleResolved = async (image) => {
    setError('');
    try {
      const response = await truckService.updateServiceLogPhoto(serviceLogId, image.id, imagePayload(image, { resolved: !image.resolved }));
      replaceImages(images.map((item) => item.id === image.id ? response.data : item));
      setSuccess(response.data.resolved ? 'Follow-up marked as resolved.' : 'Follow-up reopened.');
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (resolveError) {
      setError(resolveError?.response?.data?.error || 'Failed to update the follow-up status.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteImage || deleting) return;
    setDeleting(true);
    setError('');
    try {
      await truckService.deleteServiceLogPhoto(serviceLogId, deleteImage.id);
      replaceImages(images.filter((image) => image.id !== deleteImage.id));
      setDeleteImage(null);
      setSuccess('Repair image removed. The service log was not changed.');
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (deleteError) {
      setError(deleteError?.response?.data?.error || 'Failed to remove the repair image.');
    } finally {
      setDeleting(false);
    }
  };

  const openPreview = async (image) => {
    setPreviewImage(image);
    setPreviewLoading(true);
    setError('');
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    try {
      const response = await truckService.getServiceLogPhotoContent(serviceLogId, image.id);
      setPreviewUrl(URL.createObjectURL(response.data));
    } catch (previewError) {
      setError(previewError?.response?.data?.error || 'Failed to open the full-size image.');
      setPreviewImage(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadImage = async (image) => {
    setError('');
    try {
      const response = await truckService.getServiceLogPhotoContent(serviceLogId, image.id, { download: true });
      const objectUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      const base = String(image.original_filename || 'repair-image').replace(/\.[^.]+$/, '');
      anchor.href = objectUrl;
      anchor.download = `${base}.webp`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      setError(downloadError?.response?.data?.error || 'Failed to download the repair image.');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, borderColor: '#cbd9eb', bgcolor: '#fbfdff' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 900, color: '#001f3f' }}>
            <AddPhotoAlternateIcon color="primary" /> Images / Attachments
          </Typography>
          <Typography variant="caption" color="text.secondary">Repair evidence and items that need attention during a future service.</Typography>
        </Box>
        <Chip size="small" label={`${images.length}/${MAX_IMAGES} saved`} color={images.some((image) => image.requires_attention && !image.resolved) ? 'warning' : 'default'} sx={{ fontWeight: 800 }} />
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 1.5 }}>{success}</Alert>}

      <Box
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => { event.preventDefault(); if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
        onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(event.dataTransfer.files); }}
        sx={{
          border: '2px dashed',
          borderColor: dragging ? '#1976d2' : '#b8c9dd',
          bgcolor: dragging ? '#eef7ff' : '#f7faff',
          borderRadius: 2.5,
          p: { xs: 1.5, sm: 2 },
          textAlign: 'center',
          transition: 'all .2s ease',
        }}
      >
        <UploadFileIcon sx={{ fontSize: 34, color: dragging ? '#1976d2' : '#64748b' }} />
        <Typography sx={{ fontWeight: 800, color: '#001f3f' }}>Drop repair images here</Typography>
        <Typography variant="caption" color="text.secondary">JPEG, PNG, WebP, HEIC or HEIF • up to 10MB each</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" sx={{ mt: 1.25 }}>
          <Button component="label" variant="contained" startIcon={<AddPhotoAlternateIcon />} disabled={uploading || images.length + pending.length >= MAX_IMAGES}>
            Select Images
            <input hidden multiple type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(event) => { addFiles(event.target.files); event.target.value = ''; }} />
          </Button>
          <Button component="label" variant="outlined" startIcon={<CameraAltIcon />} disabled={uploading || images.length + pending.length >= MAX_IMAGES}>
            Take Photo
            <input hidden type="file" accept="image/*" capture="environment" onChange={(event) => { addFiles(event.target.files); event.target.value = ''; }} />
          </Button>
        </Stack>
      </Box>

      {pending.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 900, color: '#001f3f', mb: 1 }}>Review before upload</Typography>
          <Stack spacing={1.5}>
            {pending.map((item) => (
              <Paper key={item.key} variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderColor: item.requires_attention ? '#f59e0b' : '#d8e2ef', bgcolor: item.requires_attention ? '#fffaf0' : 'white' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <Box sx={{ width: { xs: '100%', md: 160 }, aspectRatio: '4 / 3', borderRadius: 2, overflow: 'hidden', bgcolor: '#e8eef6', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <Box component="img" src={item.previewUrl} alt={item.file.name} onError={(event) => { event.currentTarget.style.display = 'none'; }} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.file.name}</Typography>
                      <IconButton size="small" color="error" onClick={() => removePending(item.key)} disabled={uploading}><CloseIcon fontSize="small" /></IconButton>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                      <TextField select size="small" label="Image category" value={item.category} onChange={(event) => updatePending(item.key, 'category', event.target.value)}>
                        {CATEGORY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                      </TextField>
                      <TextField size="small" label="Related service item (optional)" value={item.service_item} onChange={(event) => updatePending(item.key, 'service_item', event.target.value)} />
                      <TextField size="small" label="Description / repair notes" value={item.description} onChange={(event) => updatePending(item.key, 'description', event.target.value)} multiline minRows={2} sx={{ gridColumn: { sm: '1 / -1' } }} />
                      <FormControlLabel control={<Switch checked={item.requires_attention} onChange={(event) => updatePending(item.key, 'requires_attention', event.target.checked)} color="warning" />} label="Requires Attention Next Time" sx={{ gridColumn: { sm: '1 / -1' } }} />
                      {item.requires_attention && (
                        <TextField size="small" label="Follow-up instructions" value={item.follow_up_instructions} onChange={(event) => updatePending(item.key, 'follow_up_instructions', event.target.value)} multiline minRows={2} sx={{ gridColumn: { sm: '1 / -1' } }} />
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
          {uploading && <Box sx={{ mt: 1.5 }}><LinearProgress variant="determinate" value={uploadProgress} /><Typography variant="caption" color="text.secondary">{uploadProgress < 100 ? `Uploading ${uploadProgress}%` : 'Processing and securing images…'}</Typography></Box>}
          <Button fullWidth variant="contained" startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />} onClick={uploadPending} disabled={uploading} sx={{ mt: 1.5, py: 1 }}>
            {uploading ? 'Uploading Repair Images…' : `Upload ${pending.length} Image${pending.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ py: 3, display: 'grid', placeItems: 'center', gap: 1 }}><CircularProgress size={26} /><Typography variant="body2">Loading repair images…</Typography></Box>
        ) : images.length === 0 ? (
          <Box sx={{ py: 3, px: 2, textAlign: 'center', borderRadius: 2, bgcolor: '#f4f7fb' }}>
            <ImageIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
            <Typography sx={{ fontWeight: 800, color: '#334155' }}>No repair images uploaded</Typography>
            <Typography variant="body2" color="text.secondary">Add before/after photos or document an issue for the next service.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 235px), 1fr))', gap: 1.5 }}>
            {images.map((image) => {
              const outstanding = image.requires_attention && !image.resolved;
              return (
                <Paper key={image.id} variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2.5, borderWidth: outstanding ? 2 : 1, borderColor: outstanding ? '#f59e0b' : '#d8e2ef', bgcolor: outstanding ? '#fffaf0' : 'white', boxShadow: outstanding ? '0 7px 22px rgba(245,158,11,.14)' : 'none' }}>
                  <SecureThumbnail serviceLogId={serviceLogId} image={image} onOpen={() => openPreview(image)} />
                  <Box sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="small" label={categoryLabel(image.category)} color={outstanding ? 'warning' : 'primary'} variant={outstanding ? 'filled' : 'outlined'} />
                      {outstanding && <Chip size="small" icon={<PriorityHighIcon />} label="Attention next time" color="warning" sx={{ fontWeight: 900 }} />}
                      {image.requires_attention && image.resolved && <Chip size="small" icon={<TaskAltIcon />} label="Resolved" color="success" />}
                    </Stack>
                    {image.service_item && <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, color: '#475569' }}>{image.service_item}</Typography>}
                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'pre-wrap' }}>{image.description || 'No description provided.'}</Typography>
                    {image.follow_up_instructions && <Alert severity={outstanding ? 'warning' : 'success'} icon={false} sx={{ mt: 1, py: 0.25, px: 1 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>{image.follow_up_instructions}</Typography></Alert>}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{image.uploaded_by} • {dateTimeLabel(image.created_at)}</Typography>
                    {image.resolved && <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>Resolved by {image.resolved_by || 'Unknown user'} • {dateTimeLabel(image.resolved_at)}</Typography>}
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                      <Box>
                        <Tooltip title="Preview"><IconButton size="small" onClick={() => openPreview(image)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Download"><IconButton size="small" onClick={() => downloadImage(image)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                      <Box>
                        {image.requires_attention && <Tooltip title={image.resolved ? 'Reopen follow-up' : 'Mark resolved'}><IconButton size="small" color={image.resolved ? 'warning' : 'success'} onClick={() => toggleResolved(image)}><TaskAltIcon fontSize="small" /></IconButton></Tooltip>}
                        <Tooltip title="Edit details"><IconButton size="small" onClick={() => openEdit(image)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Remove image"><IconButton size="small" color="error" onClick={() => setDeleteImage(image)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog open={Boolean(editImage)} onClose={() => !savingEdit && setEditImage(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Edit Repair Image</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField select label="Image category" value={editForm.category} onChange={(event) => setEditForm((current) => ({ ...current, category: event.target.value }))}>
              {CATEGORY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
            <TextField label="Related service item (optional)" value={editForm.service_item} onChange={(event) => setEditForm((current) => ({ ...current, service_item: event.target.value }))} />
            <TextField label="Description / repair notes" value={editForm.description} onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))} multiline minRows={3} />
            <FormControlLabel control={<Switch checked={editForm.requires_attention} onChange={(event) => setEditForm((current) => ({ ...current, requires_attention: event.target.checked, resolved: event.target.checked ? current.resolved : false }))} color="warning" />} label="Requires Attention Next Time" />
            {editForm.requires_attention && <TextField label="Follow-up instructions" value={editForm.follow_up_instructions} onChange={(event) => setEditForm((current) => ({ ...current, follow_up_instructions: event.target.value }))} multiline minRows={3} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditImage(null)} disabled={savingEdit}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save Changes'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteImage)} onClose={() => !deleting && setDeleteImage(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>Remove Repair Image?</DialogTitle>
        <DialogContent><Typography>This removes only the selected image and its follow-up information. The service log remains unchanged.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteImage(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Removing…' : 'Remove Image'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(previewImage)} onClose={() => { setPreviewImage(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 900 }}>Repair Image</Typography><Typography variant="caption" color="text.secondary" noWrap>{previewImage?.original_filename}</Typography></Box>
          <IconButton onClick={() => { setPreviewImage(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#0f172a', p: { xs: 1, sm: 2 }, minHeight: 280, display: 'grid', placeItems: 'center' }}>
          {previewLoading ? <CircularProgress sx={{ color: 'white' }} /> : previewUrl && <Box component="img" src={previewUrl} alt={previewImage?.description || 'Repair image'} sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />}
        </DialogContent>
        <DialogActions><Button startIcon={<DownloadIcon />} onClick={() => previewImage && downloadImage(previewImage)}>Download</Button><Button variant="contained" onClick={() => { setPreviewImage(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }}>Close</Button></DialogActions>
      </Dialog>
    </Paper>
  );
}
